import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import { accountsApi, type MemberSession } from "./accountsApi";

interface AccountValue {
  readonly session: MemberSession | null;
  /** True until the first session check finishes, so the UI can avoid flicker. */
  readonly loading: boolean;
  readonly signIn: (username: string, password: string) => Promise<void>;
  readonly register: (username: string, password: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
  /**
   * Forget a session the server has already stopped honouring.
   *
   * `signOut` is the deliberate act and calls the API; this is the answer to a
   * 401 arriving mid-session — the session is gone, so there is nothing to end,
   * and asking the server to end it would only fail a second time.
   */
  readonly clearSession: () => void;
}

const AccountContext = createContext<AccountValue | null>(null);

/**
 * Holds the signed-in member for the whole app.
 *
 * One shared piece of state rather than per-page fetching: a future project
 * page should be able to ask "who is signed in?" without repeating this, and
 * two components asking at once should not mean two requests.
 */
export function AccountProvider({ children }: { readonly children: ReactNode }) {
  const [session, setSession] = useState<MemberSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    accountsApi
      .session(controller.signal)
      .then((found) => {
        if (active) setSession(found);
      })
      .catch(() => {
        // A 401 is the ordinary answer for a signed-out visitor, and anything
        // else must not block the page either — the archive reads fine signed
        // out. Either way the session stays null, which it already is.
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  const signIn = useCallback(async (username: string, password: string) => {
    setSession(await accountsApi.login(username, password));
  }, []);

  const register = useCallback(async (username: string, password: string) => {
    setSession(await accountsApi.register(username, password));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await accountsApi.logout();
    } finally {
      // Whatever the server said, this browser is done with the session.
      setSession(null);
    }
  }, []);

  const clearSession = useCallback(() => setSession(null), []);

  const value = useMemo<AccountValue>(
    () => ({ session, loading, signIn, register, signOut, clearSession }),
    [session, loading, signIn, register, signOut, clearSession],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountValue {
  const value = useContext(AccountContext);
  if (!value) throw new Error("useAccount must be used inside an AccountProvider");
  return value;
}
